import styled from '@emotion/styled';
import { useMemo } from 'react';

import { SortDirection } from '@/api/types';
import type {
  TransactionListItem,
  TransactionSortDirection,
  TransactionSortField,
} from '@/features/transactions/utils/transaction-list';
import {
  EmptyState,
  getTableMinWidth,
  Table,
  type TableColumn,
  type TableRow,
} from '@/shared/design-system';

import {
  TransactionActionsCell,
  TransactionAmountCell,
  TransactionCounterpartyCell,
  TransactionDateCell,
  TransactionDenominationCell,
  TransactionDirectionCell,
  TransactionTypeCell,
} from './transaction-table-cells';

interface TransactionTableProps {
  transactions: TransactionListItem[];
  isLoading: boolean;
  hasNextPage: boolean;
  sortField: TransactionSortField;
  sortDirection: TransactionSortDirection;
  onSort: (_field: TransactionSortField) => void;
  onSelect: (_id: string) => void;
}

interface TransactionRow extends TableRow {
  transaction: TransactionListItem;
}

const TransactionTableArea = styled('div')(({ theme }) => ({
  '& [data-row-key] > [role="cell"]': {
    height: 'auto',
    minHeight: 72,
    paddingBlock: theme.spacing[12],
  },
}));

const getColumns = (onSelect: (_id: string) => void): TableColumn<TransactionRow>[] => [
  {
    key: 'direction',
    header: 'Direction',
    sortable: true,
    minWidth: 140,
    cell: ({ transaction }) => <TransactionDirectionCell transaction={transaction} />,
  },
  {
    key: 'fund',
    header: 'To/From',
    sortable: true,
    minWidth: 180,
    cell: ({ transaction }) => <TransactionCounterpartyCell transaction={transaction} />,
  },
  {
    key: 'date',
    header: 'Date',
    sortable: true,
    minWidth: 170,
    cell: ({ transaction }) => <TransactionDateCell transaction={transaction} />,
  },
  {
    key: 'amount',
    header: 'Amount',
    sortable: true,
    minWidth: 270,
    cell: ({ transaction }) => <TransactionAmountCell transaction={transaction} />,
  },
  {
    key: 'denomination',
    header: 'Denomination',
    minWidth: 180,
    cell: ({ transaction }) => <TransactionDenominationCell transaction={transaction} />,
  },
  {
    key: 'type',
    header: 'Type',
    sortable: true,
    minWidth: 150,
    cell: ({ transaction }) => <TransactionTypeCell transaction={transaction} />,
  },
  {
    key: 'notes',
    header: 'Actions',
    align: 'right',
    width: 150,
    cell: ({ transaction }) => (
      <TransactionActionsCell transaction={transaction} onSelect={onSelect} />
    ),
  },
];

const sortableFields = new Set<TransactionSortField>([
  'direction',
  'fund',
  'date',
  'amount',
  'type',
]);

export const TransactionTable = ({
  transactions,
  isLoading,
  hasNextPage,
  sortField,
  sortDirection,
  onSort,
  onSelect,
}: TransactionTableProps) => {
  const columns = useMemo(() => getColumns(onSelect), [onSelect]);
  const rows = useMemo<TransactionRow[]>(
    () =>
      transactions.map((transaction) => ({
        key: transaction.id,
        transaction,
        onClick: () => onSelect(transaction.id),
      })),
    [onSelect, transactions],
  );

  const handleSort = (key: string) => {
    if (sortableFields.has(key as TransactionSortField)) onSort(key as TransactionSortField);
  };

  return (
    <TransactionTableArea>
      <Table
        columns={columns}
        rows={rows}
        size="lg"
        minTableWidth={getTableMinWidth(columns)}
        isLoading={isLoading}
        emptyState={
          hasNextPage ? undefined : <EmptyState variant="tertiary" title="No transactions found" />
        }
        sortKey={sortField}
        sortDirection={sortDirection === 'asc' ? SortDirection.ASC : SortDirection.DESC}
        onSort={handleSort}
      />
    </TransactionTableArea>
  );
};
